import type { ClusterSize } from '@/composables/useMapGeometry';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { Cluster } from '~/core/entities/api/ApiClusters';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { GeocodeResult } from '~/utils/geocoding';

import { calculateClusterSizes } from '@/composables/useMapGeometry';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import { initializeMapState } from '~/composables/useMapListing';
import { EventBusKeys } from '~/core/helpers/symbols';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { createSafeDebouncedFunction } from '~/utils';
import {
	buildLocationAddress,
	geocodeAddress,
	getMacroregionAddress
} from '~/utils/geocoding';

const MAP_ZOOM_CONSTRAINTS = {
	DEFAULT: 13,
	MIN_AFTER_BOUNDS: 12,
	FIT_BOUNDS_PADDING: 7
} as const;

const DEBOUNCE_DELAYS = {
	URL_UPDATE: 300,
	MAP_BOUNDS: 0,
	MAX_WAIT: 800
} as const;

const IDLE_TIMEOUT_MS = 5000;

const DEFAULT_MAP_ZOOM = 15;

interface MapControllerConfig {
	filters: Ref<PropertyFilters>;
	gmapRef: Ref<{
		mapInstance?: google.maps.Map;
		fitBoundsToMarkers?: (
			positions: { lat: number; lng: number }[],
			padding?: number
		) => void;
	} | null>;
	clusters: Ref<Cluster[]>;
	selectedClustersData: Ref<ReadonlySet<string>>;
	normalizeClusterCoordinates: (coordinates: [number, number]) => {
		lat: number;
		lng: number;
	};
	originalUpdateClusters: (filtersData: PropertyFilters) => Promise<void>;
	pageConfig: {
		page: 'imoveis' | 'colecoes';
		collection?: string;
		wishlistId?: string;
	};
}

export enum MapState {
	INITIALIZING = 'initializing',
	READY = 'ready',
	UPDATING_LOCATION = 'updating-location',
	UPDATING_SEE_ALL_CITY = 'updating-see-all-city',
	UPDATING_FROM_INTERACTION = 'updating-from-interaction',
	APPLYING_FIT_BOUNDS = 'applying-fit-bounds',
	NAVIGATING_BY_CLUSTER = 'navigating-by-cluster'
}

interface MapControllerState {
	currentState: MapState;
	currentZoom: number;
	pendingOperations: {
		fitBoundsCallback: (() => void) | null;
		clustersUpdate: PropertyFilters | null;
	};
	locationChangeOrigin: 'filters-bar' | 'initialization' | null;
	isProgrammaticOperation: boolean;
}

interface LocationChangeData {
	lat?: number;
	lng?: number;
	regions?: string[];
	cities?: string[];
	states?: string[];
	macroregions?: string[];
}

function getArrayLength(arr: any[] | undefined): number {
	return arr?.length || 0;
}

function hasArrayAdditions(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return getArrayLength(newArr) > getArrayLength(oldArr);
}

function hasArrayRemovals(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return getArrayLength(newArr) < getArrayLength(oldArr);
}

function hasFirstItemChanged(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return newArr?.[0] !== oldArr?.[0] && !!newArr?.[0];
}

function hasLocationFieldsAdditions(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasArrayAdditions(newFilters.regions, currentFilters.regions) ||
		hasArrayAdditions(newFilters.cities, currentFilters.cities) ||
		hasArrayAdditions(newFilters.macroregions, currentFilters.macroregions) ||
		(!currentFilters.addressSlug && !!newFilters.addressSlug) ||
		(!currentFilters.poiLocationLat && !!newFilters.poiLocationLat)
	);
}

function hasLocationFieldsSubstitutions(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasFirstItemChanged(newFilters.regions, currentFilters.regions) ||
		hasFirstItemChanged(newFilters.cities, currentFilters.cities) ||
		hasFirstItemChanged(newFilters.states, currentFilters.states)
	);
}

function hasLocationFieldsRemovals(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasArrayRemovals(newFilters.regions, currentFilters.regions) ||
		hasArrayRemovals(newFilters.cities, currentFilters.cities) ||
		hasArrayRemovals(newFilters.macroregions, currentFilters.macroregions) ||
		(!!currentFilters.addressSlug && !newFilters.addressSlug) ||
		(!!currentFilters.poiLocationLat && !newFilters.poiLocationLat)
	);
}

function hasMacroregionSubstitution(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		newFilters.macroregions?.[0] !== currentFilters.macroregions?.[0] &&
		!!newFilters.macroregions?.[0] &&
		!!currentFilters.macroregions?.[0]
	);
}

function hasPoiLocationAddition(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return !!newFilters.poiLocationLat && !currentFilters.poiLocationLat;
}

function requiresGeocodingForLocationChange(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	const hasAdditions = hasLocationFieldsAdditions(newFilters, currentFilters);
	const hasSubstitutions = hasLocationFieldsSubstitutions(
		newFilters,
		currentFilters
	);
	const hasMacroregionChange = hasMacroregionSubstitution(
		newFilters,
		currentFilters
	);
	const hasPoiAddition = hasPoiLocationAddition(newFilters, currentFilters);
	const hasRemovals = hasLocationFieldsRemovals(newFilters, currentFilters);

	if (
		hasRemovals &&
		!hasAdditions &&
		!hasSubstitutions &&
		!hasMacroregionChange &&
		!hasPoiAddition
	) {
		return false;
	}

	return (
		hasAdditions || hasSubstitutions || hasMacroregionChange || hasPoiAddition
	);
}

function hasLocationFiltersChanged(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		requiresGeocodingForLocationChange(newFilters, currentFilters) ||
		hasLocationFieldsRemovals(newFilters, currentFilters)
	);
}

function hasSignificantLocationChange(
	newData: LocationChangeData,
	oldData: LocationChangeData
): boolean {
	const newFilters = {
		regions: newData.regions,
		cities: newData.cities,
		states: newData.states,
		macroregions: newData.macroregions,
		poiLocationLat: newData.lat,
		addressSlug: undefined
	} as PropertyFilters;

	const oldFilters = {
		regions: oldData.regions,
		cities: oldData.cities,
		states: oldData.states,
		macroregions: oldData.macroregions,
		poiLocationLat: oldData.lat,
		addressSlug: undefined
	} as PropertyFilters;

	return requiresGeocodingForLocationChange(newFilters, oldFilters);
}

function shouldApplyFitBoundsForLocation(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return requiresGeocodingForLocationChange(newFilters, currentFilters);
}

export function useMapController(config: MapControllerConfig) {
	const {
		filters,
		gmapRef,
		clusters,
		selectedClustersData,
		normalizeClusterCoordinates,
		originalUpdateClusters
	} = config;

	const router = useRouter();
	const { init: initGoogleMaps } = useGoogleMaps();
	const mapState = useState<PropertiesListingMapState>(
		'properties-listing-map',
		initializeMapState
	);

	const state = reactive<MapControllerState>({
		currentState: MapState.INITIALIZING,
		currentZoom: mapState.value.mapConfig.zoom || DEFAULT_MAP_ZOOM,
		pendingOperations: {
			fitBoundsCallback: null,
			clustersUpdate: null
		},
		locationChangeOrigin: null,
		isProgrammaticOperation: false
	});

	const clusterSizes = ref<Map<string, ClusterSize>>(new Map());

	const isInitializing = computed(
		() => state.currentState === MapState.INITIALIZING
	);
	const isReady = computed(() => state.currentState === MapState.READY);
	const isUpdatingLocation = computed(
		() => state.currentState === MapState.UPDATING_LOCATION
	);
	const isUpdatingSeeAllCity = computed(
		() => state.currentState === MapState.UPDATING_SEE_ALL_CITY
	);
	const isUpdatingFromInteraction = computed(
		() => state.currentState === MapState.UPDATING_FROM_INTERACTION
	);
	const isApplyingFitBounds = computed(
		() => state.currentState === MapState.APPLYING_FIT_BOUNDS
	);
	const isNavigatingByCluster = computed(
		() => state.currentState === MapState.NAVIGATING_BY_CLUSTER
	);

	function transitionTo(newState: MapState): void {
		state.currentState = newState;
	}

	function canUpdateClusters(): boolean {
		return state.currentState !== MapState.UPDATING_LOCATION;
	}

	async function applyGeocodeResultToMap(
		result: GeocodeResult,
		shouldFitBounds: boolean = true
	): Promise<void> {
		if (!gmapRef.value?.mapInstance) return;

		state.isProgrammaticOperation = true;

		const center = result.bounds ? result.bounds.getCenter() : result.center;
		if (!center) return;

		return new Promise(resolve => {
			let idleListener: google.maps.MapsEventListener | null = null;

			const timeoutId = setTimeout(() => {
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}
				resolve();
			}, IDLE_TIMEOUT_MS);

			try {
				if (!gmapRef.value?.mapInstance) {
					clearTimeout(timeoutId);
					resolve();
					return;
				}

				idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
					clearTimeout(timeoutId);
					if (idleListener) {
						google.maps.event.removeListener(idleListener);
						idleListener = null;
					}

					if (!gmapRef.value?.mapInstance) {
						resolve();
						return;
					}

					mapState.value.mapConfig.center = center;

					if (result.bounds && shouldFitBounds) {
						mapState.value.mapConfig.bounds = result.bounds;
						const zoom = gmapRef.value.mapInstance.getZoom();
						if (zoom && zoom < MAP_ZOOM_CONSTRAINTS.MIN_AFTER_BOUNDS) {
							gmapRef.value.mapInstance.setZoom(
								MAP_ZOOM_CONSTRAINTS.MIN_AFTER_BOUNDS
							);
						}
					} else if (!shouldFitBounds) {
						mapState.value.mapConfig.bounds = undefined;
						gmapRef.value.mapInstance.setZoom(15);
					}

					resolve();
				});

				gmapRef.value.mapInstance.setCenter(center);
			} catch (error) {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
				}
				console.error('Error in applyGeocodeResultToMap:', error);
				resolve();
			}
		});
	}

	async function fitMapFromMacroregion(
		macroregion: string,
		shouldFitBounds: boolean = true
	): Promise<void> {
		const address = getMacroregionAddress(macroregion);
		if (!address) return;

		const result = await geocodeAddress(address);
		await applyGeocodeResultToMap(result, shouldFitBounds);
	}

	async function fitMapFromLocation(
		region: string,
		city: string,
		state: string,
		shouldFitBounds: boolean = true
	): Promise<void> {
		const address = buildLocationAddress(region, city, state);
		const result = await geocodeAddress(address);

		if (result.center || result.bounds) {
			await applyGeocodeResultToMap(result, shouldFitBounds);
		}
	}

	function fitMapFromPoi(lat: number, lng: number): Promise<void> {
		if (!gmapRef.value?.mapInstance) return Promise.resolve();

		state.isProgrammaticOperation = true;

		return new Promise(resolve => {
			let idleListener: google.maps.MapsEventListener | null = null;

			const timeoutId = setTimeout(() => {
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}
				resolve();
			}, IDLE_TIMEOUT_MS);

			try {
				if (!gmapRef.value?.mapInstance) {
					clearTimeout(timeoutId);
					resolve();
					return;
				}

				idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
					clearTimeout(timeoutId);
					if (idleListener) {
						google.maps.event.removeListener(idleListener);
						idleListener = null;
					}

					mapState.value.mapConfig.center = { lat, lng };
					const currentZoom = gmapRef.value?.mapInstance?.getZoom();
					if (!currentZoom || currentZoom < 15) {
						gmapRef.value!.mapInstance!.setZoom(15);
					}
					resolve();
				});

				gmapRef.value.mapInstance.setCenter({ lat, lng });
			} catch (error) {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
				}
				console.error('Error in fitMapFromPoi:', error);
				resolve();
			}
		});
	}

	function applyUrlBoundsToMap(): void {
		if (
			!gmapRef.value?.mapInstance ||
			!filters.value.zoom ||
			!filters.value.bounds
		) {
			return;
		}

		state.isProgrammaticOperation = true;

		const boundsArray = filters.value.bounds.split(',').map(Number);
		if (boundsArray.length === 4 && boundsArray.every(num => !isNaN(num))) {
			const [west, south, east, north] = boundsArray;

			const centerLat = (south! + north!) / 2;
			const centerLng = (west! + east!) / 2;

			gmapRef.value.mapInstance.setZoom(filters.value.zoom);
			gmapRef.value.mapInstance.setCenter({ lat: centerLat, lng: centerLng });
		}
	}

	function fitBoundsToMarkers(): void {
		state.isProgrammaticOperation = true;

		if (clusters.value.length > 0 && gmapRef.value?.fitBoundsToMarkers) {
			const positions = clusters.value.map(cluster =>
				normalizeClusterCoordinates(cluster.geometry.coordinates)
			);

			gmapRef.value.fitBoundsToMarkers(
				positions,
				MAP_ZOOM_CONSTRAINTS.FIT_BOUNDS_PADDING
			);
		}

		nextTick(() => {
			transitionTo(MapState.READY);
			state.pendingOperations.fitBoundsCallback?.();
			state.pendingOperations.fitBoundsCallback = null;
		});
	}

	function updateClusterSizes(): void {
		if (!gmapRef.value?.mapInstance) return;

		const currentZoomValue =
			gmapRef.value.mapInstance.getZoom() ?? MAP_ZOOM_CONSTRAINTS.DEFAULT;
		clusterSizes.value = calculateClusterSizes(
			clusters.value,
			currentZoomValue
		);
	}

	async function initializeMapFromCurrentFilters(
		shouldFitBounds: boolean = true
	): Promise<void> {
		if (import.meta.server) return;

		// Ensure Google Maps API is loaded before geocoding operations
		await initGoogleMaps();

		const {
			poiLocationLat,
			poiLocationLng,
			locationSlug,
			regions,
			cities,
			states,
			macroregions
		} = filters.value;

		if (poiLocationLat && poiLocationLng) {
			await fitMapFromPoi(poiLocationLat, poiLocationLng);
			return;
		}

		const MACROREGION_SLUGS: Record<string, string> = {
			'sao-paulo-sp-brasil': 'São Paulo',
			'curitiba-pr-brasil': 'Curitiba'
		};

		if (locationSlug && MACROREGION_SLUGS[locationSlug]) {
			await fitMapFromMacroregion(
				MACROREGION_SLUGS[locationSlug],
				shouldFitBounds
			);
			return;
		}

		if (regions?.[0] || cities?.[0]) {
			await fitMapFromLocation(
				regions?.[0] || '',
				cities?.[0] || '',
				states?.[0] || '',
				shouldFitBounds
			);
			return;
		}

		if (macroregions?.[0]) {
			await fitMapFromMacroregion(macroregions[0], shouldFitBounds);
		}
	}

	function processLocationFiltersChange(
		origin: 'filters-bar' | 'initialization' = 'initialization'
	): void {
		state.locationChangeOrigin = origin;

		transitionTo(MapState.UPDATING_LOCATION);
	}

	function processSeeAllCity(
		origin: 'filters-bar' | 'initialization' = 'initialization'
	): void {
		state.locationChangeOrigin = origin;

		transitionTo(MapState.UPDATING_SEE_ALL_CITY);
	}

	function finishLocationProcessing(): void {
		if (state.currentState === MapState.READY) {
			return;
		}

		state.locationChangeOrigin = null;
		state.isProgrammaticOperation = false;
		transitionTo(MapState.READY);

		nextTick(() => {
			state.pendingOperations.fitBoundsCallback?.();
			state.pendingOperations.fitBoundsCallback = null;
		});
	}

	async function handleRegionChanged(shouldFitBounds = false): Promise<void> {
		if (isInitializing.value) return;

		if (shouldFitBounds) {
			state.isProgrammaticOperation = true;
			transitionTo(MapState.APPLYING_FIT_BOUNDS);
			processLocationFiltersChange('filters-bar');
		}
	}

	function handleDragEnd(): void {
		if (
			!gmapRef.value?.mapInstance ||
			!isReady.value ||
			state.isProgrammaticOperation
		) {
			return;
		}

		transitionTo(MapState.UPDATING_FROM_INTERACTION);

		let idleListener: google.maps.MapsEventListener | null = null;

		const timeoutId = setTimeout(() => {
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
				idleListener = null;
			}
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}, IDLE_TIMEOUT_MS);

		try {
			if (!gmapRef.value?.mapInstance) {
				clearTimeout(timeoutId);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
				return;
			}

			idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}

				const bounds = gmapRef.value?.mapInstance?.getBounds();
				if (!bounds) {
					nextTick(() => {
						transitionTo(MapState.READY);
					});
					return;
				}

				debouncedUpdateMapBounds(bounds);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
			});
		} catch (error) {
			clearTimeout(timeoutId);
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
			}
			console.error('Error in handleDragEnd:', error);
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}
	}

	function handleZoomChanged(zoom: number | undefined): void {
		if (!gmapRef.value?.mapInstance || !zoom) return;

		const previousZoom = state.currentZoom;
		state.currentZoom = zoom;
		updateClusterSizes();

		if (!state.isProgrammaticOperation) {
			// If current zoom is less than 16, always clear selections
			if (
				zoom < 16 &&
				selectedClustersData.value.size > 0 &&
				filters.value.h3Ids &&
				filters.value.h3Ids.length > 0
			) {
				clusters.value = [];
				(selectedClustersData.value as Set<string>).clear();

				const newFiltersData = {
					...filters.value,
					h3Ids: [],
					bounds: filters.value.bounds,
					zoom: zoom
				} as PropertyFilters;

				router.push(
					PropertyFiltersPresenter.getPathFromFilters(
						newFiltersData,
						{
							regions: filters.value.regions,
							cities: filters.value.cities,
							states: filters.value.states,
							macroregions: filters.value.macroregions
						},
						{ page: 'imoveis' }
					)
				);

				forceUpdateClusters(newFiltersData);
				return;
			}

			// For zooms 16+, keep context logic
			const wasInHighZoomContext = previousZoom >= 16;
			const isInHighZoomContext = zoom >= 16;

			const shouldClearSelection =
				wasInHighZoomContext !== isInHighZoomContext &&
				selectedClustersData.value.size > 0 &&
				filters.value.h3Ids &&
				filters.value.h3Ids.length > 0;

			if (shouldClearSelection) {
				clusters.value = [];
				(selectedClustersData.value as Set<string>).clear();

				const newFiltersData = {
					...filters.value,
					h3Ids: [],
					bounds: filters.value.bounds,
					zoom: zoom
				} as PropertyFilters;

				router.push(
					PropertyFiltersPresenter.getPathFromFilters(
						newFiltersData,
						{
							regions: filters.value.regions,
							cities: filters.value.cities,
							states: filters.value.states,
							macroregions: filters.value.macroregions
						},
						{ page: 'imoveis' }
					)
				);

				forceUpdateClusters(newFiltersData);
				return;
			}

			// Clear visual clusters when zoom decreases, except between 16-17
			if (
				zoom < previousZoom &&
				!(wasInHighZoomContext && isInHighZoomContext)
			) {
				clusters.value = [];
			}
		}

		if (
			isInitializing.value ||
			!isReady.value ||
			state.isProgrammaticOperation
		) {
			return;
		}

		transitionTo(MapState.UPDATING_FROM_INTERACTION);

		let idleListener: google.maps.MapsEventListener | null = null;

		const timeoutId = setTimeout(() => {
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
				idleListener = null;
			}
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}, IDLE_TIMEOUT_MS);

		try {
			if (!gmapRef.value?.mapInstance) {
				clearTimeout(timeoutId);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
				return;
			}

			idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}

				const bounds = gmapRef.value?.mapInstance?.getBounds();
				if (!bounds) {
					nextTick(() => {
						transitionTo(MapState.READY);
					});
					return;
				}

				debouncedUpdateMapBounds(bounds);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
			});
		} catch (error) {
			clearTimeout(timeoutId);
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
			}
			console.error('Error in handleZoomChanged:', error);
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}
	}

	function handleAutoZoom(zoomLevel: number): void {
		if (!gmapRef.value?.mapInstance) return;

		state.isProgrammaticOperation = true;
		gmapRef.value.mapInstance.setZoom(zoomLevel);
		state.currentZoom = zoomLevel;
	}

	function handleClusterNavigation(): void {
		transitionTo(MapState.NAVIGATING_BY_CLUSTER);

		nextTick(() => {
			transitionTo(MapState.READY);
		});
	}

	function initializeMapFromUrlBounds(): void {
		nextTick(() => {
			applyUrlBoundsToMap();
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		});
	}

	function initializeMapWithFitBounds(): void {
		transitionTo(MapState.APPLYING_FIT_BOUNDS);
	}

	async function handleInitialMapLoad(): Promise<void> {
		const hasUrlBounds = Boolean(filters.value.zoom && filters.value.bounds);

		if (hasUrlBounds) {
			initializeMapFromUrlBounds();
		} else if (config.pageConfig.wishlistId) {
			const filtersToUse = {
				...filters.value,
				wishlistId: config.pageConfig.wishlistId
			};
			await originalUpdateClusters(filtersToUse);
			initializeMapWithFitBounds();
		} else {
			// Initialize map from current filters first to ensure proper center
			await initializeMapFromCurrentFilters();
			// Load clusters before fitting bounds
			await originalUpdateClusters(filters.value);
			initializeMapWithFitBounds();
		}
	}

	async function processPoiLocationChange(
		lat: number,
		lng: number
	): Promise<void> {
		await fitMapFromPoi(lat, lng);
		finishLocationProcessing();
	}

	async function processLocationChange(
		newData: LocationChangeData
	): Promise<void> {
		const { lat, lng } = newData;

		if (lat && lng) {
			await processPoiLocationChange(lat, lng);
			return;
		}
	}

	watch(
		() => ({
			lat: filters.value.poiLocationLat,
			lng: filters.value.poiLocationLng,
			regions: filters.value.regions,
			cities: filters.value.cities,
			states: filters.value.states,
			macroregions: filters.value.macroregions
		}),
		async (newLocationData, oldLocationData) => {
			if (!isUpdatingLocation.value || !oldLocationData) return;
			if (!hasSignificantLocationChange(newLocationData, oldLocationData))
				return;

			await processLocationChange(newLocationData);
		},
		{ immediate: false }
	);

	function getBoundsString(bounds: google.maps.LatLngBounds): string {
		const { north, south, east, west } = bounds.toJSON();
		return `${west},${south},${east},${north}`;
	}

	function updateMapClusters(
		bounds?: google.maps.LatLngBounds,
		updatedFilters?: PropertyFilters
	): PropertyFilters {
		const newFilters = updatedFilters || { ...filters.value };
		const boundsString = bounds ? getBoundsString(bounds) : undefined;
		const currentZoom = bounds
			? gmapRef.value?.mapInstance?.getZoom()
			: undefined;
		const hasSelectedClusters =
			selectedClustersData.value.size > 0 &&
			newFilters.h3Ids &&
			newFilters.h3Ids.length > 0;

		const clustersFiltersData = {
			...newFilters,
			bounds: boundsString,
			zoom: currentZoom
		} as PropertyFilters;

		updateClusters(clustersFiltersData);

		return {
			...newFilters,
			bounds: boundsString,
			h3Ids: hasSelectedClusters ? newFilters.h3Ids : [],
			zoom: currentZoom
		} as PropertyFilters;
	}

	async function updateUrlAndListing(
		filtersData: PropertyFilters
	): Promise<void> {
		const { PropertyFiltersPresenter } =
			await import('~/presenters/PropertyFiltersPresenter');

		router.push(
			PropertyFiltersPresenter.getPathFromFilters(
				filtersData,
				{
					regions: filtersData.regions,
					cities: filtersData.cities,
					states: filtersData.states,
					macroregions: filtersData.macroregions
				},
				config.pageConfig
			)
		);
	}

	function updateMapBounds(
		bounds?: google.maps.LatLngBounds,
		updatedFilters?: PropertyFilters
	): void {
		const sharedFiltersData = updateMapClusters(bounds, updatedFilters);

		if ((sharedFiltersData as any).skipListingUpdate) {
			return;
		}

		updateUrlAndListing(sharedFiltersData);
	}

	const debouncedUpdateMapBounds = createSafeDebouncedFunction(
		updateMapBounds,
		DEBOUNCE_DELAYS.MAP_BOUNDS,
		{
			maxWait: DEBOUNCE_DELAYS.MAX_WAIT
		}
	);

	const updateClusters = (filtersData: PropertyFilters) => {
		if (!canUpdateClusters()) {
			state.pendingOperations.clustersUpdate = filtersData;
			return;
		}
		originalUpdateClusters(filtersData);
	};

	const forceUpdateClusters = (filtersData: PropertyFilters) => {
		originalUpdateClusters(filtersData);
	};

	const clusterStateHandlers = {
		[MapState.APPLYING_FIT_BOUNDS]: (clusters: Cluster[]) => {
			if (clusters?.length > 0) {
				fitBoundsToMarkers();
			}
		},
		[MapState.UPDATING_LOCATION]: (clusters: Cluster[]) => {
			if (clusters?.length > 0) {
				fitBoundsToMarkers();
			} else {
				initializeMapFromCurrentFilters(false).then(() => {
					finishLocationProcessing();
				});
			}
		},
		[MapState.UPDATING_SEE_ALL_CITY]: (clusters: Cluster[]) => {
			finishLocationProcessing();
		},
		[MapState.INITIALIZING]: () => {
			transitionTo(MapState.READY);
		}
	} as const;

	function handleClusterStateChange(newClusters: Cluster[]): void {
		const handler =
			clusterStateHandlers[
				state.currentState as keyof typeof clusterStateHandlers
			];

		if (handler) {
			handler(newClusters);
		} else if (isInitializing.value) {
			clusterStateHandlers[MapState.INITIALIZING]();
		}

		if (state.pendingOperations.clustersUpdate) {
			const pendingUpdate = state.pendingOperations.clustersUpdate;
			state.pendingOperations.clustersUpdate = null;
			updateClusters(pendingUpdate);
		}

		updateClusterSizes();
	}

	watch(
		() => clusters.value,
		newClusters => {
			nextTick(() => {
				handleClusterStateChange(newClusters);
			});
		},
		{ immediate: true }
	);

	watch(
		() => state.currentState,
		(newState, oldState) => {
			if (newState === MapState.READY && oldState === MapState.INITIALIZING) {
				const hasUrlBounds = Boolean(
					filters.value.zoom && filters.value.bounds
				);

				if (hasUrlBounds) {
					const clustersFiltersData = {
						...filters.value,
						bounds: filters.value.bounds,
						zoom: filters.value.zoom
					} as PropertyFilters;
					updateClusters(clustersFiltersData);
				} else {
					updateClusters(filters.value);
				}
			}
		}
	);

	const eventCleanupFunctions: (() => void)[] = [];

	const regionChangedBus = useEventBus<boolean>(EventBusKeys.regionChanged);
	const autoZoomBus = useEventBus<number>(EventBusKeys.autoZoom);

	eventCleanupFunctions.push(
		regionChangedBus.on(handleRegionChanged),
		autoZoomBus.on(handleAutoZoom)
	);

	function clearProgrammaticFlag(): void {
		if (state.isProgrammaticOperation) {
			state.isProgrammaticOperation = false;
		}
	}

	const stopIdleWatch = watch(
		() => gmapRef.value?.mapInstance,
		mapInstance => {
			if (!mapInstance) return;

			mapInstance.addListener('idle', () => {
				setTimeout(() => {
					clearProgrammaticFlag();
				}, 100);
			});

			stopIdleWatch();
		},
		{ immediate: true }
	);

	onUnmounted(() => {
		eventCleanupFunctions.forEach(cleanup => cleanup());
		eventCleanupFunctions.length = 0;
		clusterSizes.value.clear();
	});

	return {
		state: {
			isInitializing,
			isReady,
			isUpdatingLocation,
			isUpdatingSeeAllCity,
			isUpdatingFromInteraction,
			isApplyingFitBounds,
			isNavigatingByCluster,
			currentState: readonly(toRef(state, 'currentState')),
			currentZoom: readonly(toRef(state, 'currentZoom'))
		},
		clusterSizes: readonly(clusterSizes),

		initializeMapFromCurrentFilters,
		processLocationFiltersChange,
		processSeeAllCity,
		handleInitialMapLoad,
		initializeMapWithFitBounds,
		initializeMapFromUrlBounds,
		handleDragEnd,
		handleZoomChanged,
		handleRegionChanged,
		handleClusterNavigation,
		fitBoundsToMarkers,
		updateClusterSizes,
		hasLocationFiltersChanged,
		shouldApplyFitBoundsForLocation,
		updateClusters,
		forceUpdateClusters,
		updateMapBounds,
		clearProgrammaticFlag,
		MapState
	};
}
